import { VideoProcessor } from 'twilio-video';

/**
 * Keep browser-specific GPU surfaces out of the captured video stream. Twilio's
 * worker still segments the person, but receives a canvas-backed image and writes
 * into a private bitmap canvas. Only a normal 2D canvas is captured/published.
 */
export default function createBackgroundRenderer(
	processor: VideoProcessor,
): VideoProcessor {
	let inputCanvas: OffscreenCanvas | undefined;
	let processedCanvas: HTMLCanvasElement | undefined;
	return {
		async processFrame(input, output) {
			// addProcessor uses inputFrameBufferType: 'video'. Avoid transferring a
			// camera-backed VideoFrame directly between the browser/GPU and worker.
			const video = input as HTMLVideoElement;
			if (!video.videoWidth || !video.videoHeight) return;
			// Twilio recommends at most 640x480 for effects. Preserve aspect ratio,
			// including portrait cameras, without restarting the user's camera.
			const scale = Math.min(
				1,
				640 / video.videoWidth,
				480 / video.videoHeight,
			);
			const width = Math.max(1, Math.round(video.videoWidth * scale));
			const height = Math.max(1, Math.round(video.videoHeight * scale));
			inputCanvas ??= new OffscreenCanvas(width, height);
			processedCanvas ??= document.createElement('canvas');
			for (const canvas of [inputCanvas, processedCanvas]) {
				if (canvas.width !== width) canvas.width = width;
				if (canvas.height !== height) canvas.height = height;
			}
			const inputContext = inputCanvas.getContext(
				'2d',
			) as OffscreenCanvasRenderingContext2D | null;
			const outputContext = output.getContext('2d');
			if (
				!inputContext ||
				!outputContext ||
				!processedCanvas.getContext('bitmaprenderer')
			) {
				throw new Error('Background rendering context unavailable');
			}
			inputContext.drawImage(video, 0, 0, width, height);
			await processor.processFrame(inputCanvas, processedCanvas);
			outputContext.clearRect(0, 0, output.width, output.height);
			outputContext.drawImage(
				processedCanvas,
				0,
				0,
				output.width,
				output.height,
			);
		},
	};
}
